import { useIsomorphicLayoutEffect } from '@pairy/react-hooks';
import { useAtom, useSetAtom } from 'jotai';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';

import { v2ZoomAtom, v2ZoomFitModeAtom } from '../../store/v2/viewer.atom';
import {
  V2_WHEEL_ZOOM_INTENSITY,
  clampV2Zoom,
} from '../../components/v2/zoom.constants';

interface AnchorState {
  el: HTMLElement;
  /** 앵커 요소 내부의 상대 좌표(0~1) */
  ratioX: number;
  ratioY: number;
  /** 줌 후에도 같은 콘텐츠 지점이 위치해야 할 뷰포트 좌표 */
  clientX: number;
  clientY: number;
  /** 커밋 시 함께 반영할 팬 이동량(핀치 중 중점 드리프트) */
  panX: number;
  panY: number;
}

interface Props {
  /** 네이티브 스크롤을 담당하는 컨테이너 */
  scrollRef: RefObject<HTMLElement>;
}

/** 포인터와 세로로 가장 가까운 [data-zoom-anchor] 요소를 찾는다 */
const findAnchorEl = (
  scrollEl: HTMLElement,
  clientX: number,
  clientY: number,
): HTMLElement | null => {
  let best: HTMLElement | null = null;
  let bestDistance = Infinity;

  scrollEl.querySelectorAll<HTMLElement>('[data-zoom-anchor]').forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return;
    }

    const distance =
      clientY < rect.top
        ? rect.top - clientY
        : clientY > rect.bottom
        ? clientY - rect.bottom
        : 0;

    if (distance < bestDistance) {
      bestDistance = distance;
      best = el;
    }
  });

  return best;
};

/**
 * 포인터 위치를 앵커로 유지하는 줌.
 * - 줌은 레이아웃(크기) 기반이므로, 커밋 직후 layout effect에서
 *   앵커 지점의 화면상 이동량만큼 스크롤을 보정해 포인터 아래
 *   콘텐츠가 고정된 것처럼 보이게 한다.
 * - Ctrl/Cmd + 휠은 연속 배율로 처리해 부드럽게 확대/축소한다.
 * - 앵커가 없는 줌 변경(툴바 ±/프리셋 등)은 보정하지 않는다(기본 동작 유지).
 */
const useAnchoredZoom = ({ scrollRef }: Props) => {
  const [zoom, setZoom] = useAtom(v2ZoomAtom);
  const setFitMode = useSetAtom(v2ZoomFitModeAtom);

  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  const pendingAnchorRef = useRef<AnchorState | null>(null);
  const pendingZoomRef = useRef<number | null>(null);
  const rafIdRef = useRef<number | null>(null);

  const zoomAtPoint = useCallback(
    (
      nextZoom: number,
      clientX: number,
      clientY: number,
      pan?: { x: number; y: number },
      options?: {
        /**
         * rAF 배칭 없이 같은 태스크에서 커밋한다. 제스처 프리뷰 transform을
         * 지운 직후 호출할 때 이전 레이아웃이 한 프레임 그려지는 것을 막는다.
         */
        immediate?: boolean;
      },
    ) => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) {
        return;
      }

      const clampedZoom = clampV2Zoom(nextZoom);

      /* 배율 변화가 없으면 리렌더가 없으므로 팬만 즉시 반영한다 */
      if (clampedZoom === zoomRef.current && pendingZoomRef.current === null) {
        if (pan) {
          scrollEl.scrollLeft -= pan.x;
          scrollEl.scrollTop -= pan.y;
        }
        return;
      }

      const anchorEl = findAnchorEl(scrollEl, clientX, clientY);
      if (anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        pendingAnchorRef.current = {
          el: anchorEl,
          ratioX: (clientX - rect.left) / rect.width,
          ratioY: (clientY - rect.top) / rect.height,
          clientX,
          clientY,
          panX: pan?.x ?? 0,
          panY: pan?.y ?? 0,
        };
      }

      pendingZoomRef.current = clampedZoom;

      const commit = () => {
        if (pendingZoomRef.current === null) {
          return;
        }
        setZoom(pendingZoomRef.current);
        setFitMode('custom');
        pendingZoomRef.current = null;
      };

      if (options?.immediate) {
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        /*
         * transitionend/touchend 같은 비이산 이벤트의 업데이트는 React가
         * 다음 태스크로 미뤄 그 사이 한 프레임이 그려질 수 있다.
         * 동기 렌더로 강제해 이전 레이아웃이 페인트되지 않게 한다.
         */
        flushSync(commit);
        return;
      }

      /* 휠 연타를 프레임당 1회 커밋으로 배칭한다 */
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null;
          commit();
        });
      }
    },
    [scrollRef, setZoom, setFitMode],
  );

  /* 줌 커밋 직후(페인트 전) 앵커 드리프트만큼 스크롤을 보정한다 */
  useIsomorphicLayoutEffect(() => {
    const anchor = pendingAnchorRef.current;
    const scrollEl = scrollRef.current;
    if (!anchor || !scrollEl) {
      return;
    }
    pendingAnchorRef.current = null;

    const rect = anchor.el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return;
    }

    scrollEl.scrollLeft +=
      rect.left + anchor.ratioX * rect.width - anchor.clientX - anchor.panX;
    scrollEl.scrollTop +=
      rect.top + anchor.ratioY * rect.height - anchor.clientY - anchor.panY;
  }, [zoom, scrollRef]);

  /* Ctrl/Cmd + 휠 연속 줌(브라우저 페이지 줌 대체) */
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return;
    }

    const handleWheel = (event: globalThis.WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }
      event.preventDefault();

      const baseZoom = pendingZoomRef.current ?? zoomRef.current;
      zoomAtPoint(
        baseZoom * Math.exp(-event.deltaY * V2_WHEEL_ZOOM_INTENSITY),
        event.clientX,
        event.clientY,
      );
    };

    scrollEl.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollEl.removeEventListener('wheel', handleWheel);
    };
  }, [scrollRef, zoomAtPoint]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return { zoomAtPoint };
};

export default useAnchoredZoom;
